import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import toast, { Toaster } from "react-hot-toast";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaPrint,
  FaShareAlt,
  FaPaperPlane,
  FaTrash,
  FaArrowLeft,
  FaUserTie,
  FaUsers,
  FaEnvelope,
  FaBuilding,
  FaCrown,
  FaCalendarAlt,
  FaSave,
  FaExclamationTriangle,
  FaGavel,
  FaEye,
} from "react-icons/fa";

/* ===========================================================
  Helpers
=========================================================== */
const safeString = (v) => (typeof v === "string" ? v.trim() : "");

const formatPhoneForWhatsapp = (phone) => {
  const cleaned = safeString(phone).replace(/[^\d]/g, "");
  if (cleaned.startsWith("0")) return `20${cleaned.slice(1)}`;
  return cleaned;
};

const formatDateAr = (date) => {
  try {
    return new Date(date).toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [clientCases, setClientCases] = useState([]);
  const [assistantsLimit, setAssistantsLimit] = useState(0);

  const [loading, setLoading] = useState(true);
  const [savingLimit, setSavingLimit] = useState(false);

  /* ===========================================================
    Fetch client + cases
  ============================================================ */
  const fetchClient = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/clients/${id}`);

      // ✅ PRO: Supports both old & new API shapes
      // new API: {success, client, cases}
      // old API: client object directly or {client:...}
      const dataClient = res.data?.client || res.data;
      const dataCases = Array.isArray(res.data?.cases) ? res.data.cases : [];

      setClient(dataClient);
      setClientCases(dataCases);

      setAssistantsLimit(Number(dataClient?.assistantsLimit || 0));
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل تحميل بيانات العميل");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  /* ===========================================================
    Derived values
  ============================================================ */
  const whatsappPhone = useMemo(
    () => formatPhoneForWhatsapp(client?.phone),
    [client]
  );

  const typeBadge = useMemo(() => {
    const t = client?.type || "—";
    const isPremium = t === "مميز" || t === "VIP";

    return {
      text: t,
      className: isPremium
        ? "bg-yellow-500 text-black"
        : "bg-gray-700 text-gray-100",
      icon: isPremium ? <FaCrown /> : <FaUserTie />,
    };
  }, [client]);

  /* ===========================================================
    Actions
  ============================================================ */
  const handleDelete = async () => {
    if (!window.confirm("⚠️ هل تريد حذف هذا العميل نهائيًا؟")) return;

    try {
      await api.delete(`/clients/${id}`);
      toast.success("🗑️ تم حذف العميل بنجاح");
      navigate("/clients");
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل حذف العميل");
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("📋 تم نسخ رابط العميل للمشاركة");
    } catch {
      toast.error("❌ فشل نسخ الرابط");
    }
  };

  const handlePrint = () => window.print();

  const saveAssistantsLimit = async () => {
    if (!client?._id) return;

    const value = Number(assistantsLimit);
    if (Number.isNaN(value) || value < 0)
      return toast.error("⚠️ الحد الأقصى يجب أن يكون رقم صحيح");

    try {
      setSavingLimit(true);
      await api.put(`/clients/${client._id}`, { assistantsLimit: value });
      toast.success("✅ تم حفظ الحد الأقصى للمساعدين");
      fetchClient();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل حفظ الحد الأقصى");
    } finally {
      setSavingLimit(false);
    }
  };

  /* ===========================================================
    Loading / Error UI
  ============================================================ */
  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-900 text-white p-6">
        <Toaster position="top-center" />
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-10 w-48 bg-gray-800 rounded" />
            <div className="h-28 bg-gray-800 rounded-2xl" />
            <div className="h-28 bg-gray-800 rounded-2xl" />
            <div className="h-28 bg-gray-800 rounded-2xl" />
          </div>
        </div>
      </div>
    );

  if (!client)
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-900 text-white p-6">
        <Toaster position="top-center" />
        <div className="max-w-5xl mx-auto text-center mt-20">
          <FaExclamationTriangle className="text-yellow-400 text-5xl mx-auto mb-4" />
          <p className="text-xl text-red-400 font-bold">❌ لم يتم العثور على العميل</p>
          <button
            onClick={() => navigate("/clients")}
            className="mt-6 bg-gray-700 hover:bg-gray-600 px-5 py-3 rounded-xl font-bold"
          >
            الرجوع للعملاء
          </button>
        </div>
      </div>
    );

  /* ===========================================================
    Render
  ============================================================ */
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-900 text-white p-4 sm:p-6">
      <Toaster position="top-center" />

      <div className="max-w-5xl mx-auto">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <button
            onClick={() => navigate("/clients")}
            className="w-full sm:w-auto bg-gray-800 hover:bg-gray-700 px-5 py-3 rounded-xl flex items-center justify-center gap-2 font-bold"
          >
            <FaArrowLeft /> العودة للقائمة
          </button>

          <button
            onClick={handleDelete}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 px-5 py-3 rounded-xl flex items-center justify-center gap-2 font-bold"
          >
            <FaTrash /> حذف العميل
          </button>
        </div>

        {/* Header */}
        <div className="bg-gray-900 border border-yellow-500 rounded-2xl p-5 sm:p-6 shadow-xl mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="min-w-0">
              <p className="text-gray-400 text-sm mb-1">📌 تفاصيل العميل</p>
              <h1 className="text-2xl sm:text-3xl font-black text-yellow-400 break-words">
                {client.name}
              </h1>

              <div className="mt-3 flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${typeBadge.className}`}
                >
                  {typeBadge.icon} {typeBadge.text}
                </span>

                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800 text-gray-200 text-sm font-bold">
                  <FaCalendarAlt /> {formatDateAr(client.createdAt)}
                </span>

                {client.officeName ? (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800 text-gray-200 text-sm font-bold">
                    <FaBuilding /> {client.officeName}
                  </span>
                ) : null}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleShare}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-3 rounded-xl font-black flex items-center justify-center gap-2"
              >
                <FaShareAlt /> مشاركة
              </button>

              <button
                onClick={handlePrint}
                className="bg-gray-200 hover:bg-gray-300 text-black px-5 py-3 rounded-xl font-black flex items-center justify-center gap-2"
              >
                <FaPrint /> طباعة
              </button>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5">
            <h3 className="text-lg font-black text-yellow-300 mb-4 flex items-center gap-2">
              <FaUserTie /> بيانات التواصل
            </h3>

            <div className="space-y-3">
              <div className="bg-gray-800 p-4 rounded-xl">
                <p className="text-sm text-gray-400 mb-1 flex items-center gap-2">
                  <FaEnvelope /> رقم التوكيل
                </p>
                <p className="font-bold break-words">{client.email || "—"}</p>
              </div>

              <div className="bg-gray-800 p-4 rounded-xl">
                <p className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <FaPhoneAlt /> رقم الهاتف
                </p>
                <p className="font-bold break-words">{client.phone || "—"}</p>

                <div className="flex flex-wrap gap-2 mt-3">
                  {client.phone ? (
                    <>
                      <a
                        href={`tel:${client.phone}`}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2"
                      >
                        <FaPhoneAlt /> اتصال
                      </a>

                      <a
                        href={`https://wa.me/${whatsappPhone}`}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2"
                      >
                        <FaWhatsapp /> واتساب
                      </a>
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">
                      لا يوجد رقم هاتف مسجل
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Assistants */}
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 lg:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-black text-yellow-300 flex items-center gap-2">
                <FaUsers /> المحامين المساعدين
              </h3>

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="number"
                  min="0"
                  className="p-3 rounded-xl bg-gray-800 text-white w-full sm:w-40 outline-none border border-transparent focus:border-yellow-500"
                  value={assistantsLimit}
                  onChange={(e) => setAssistantsLimit(e.target.value)}
                  placeholder="Assistants Limit"
                />

                <button
                  onClick={saveAssistantsLimit}
                  disabled={savingLimit}
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-70 text-black px-5 py-3 rounded-xl font-black flex items-center justify-center gap-2"
                >
                  <FaSave />
                  {savingLimit ? "جاري الحفظ..." : "حفظ الحد"}
                </button>
              </div>
            </div>

            {client.assistants?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {client.assistants.map((a, i) => (
                  <div
                    key={i}
                    className="bg-gray-800 p-4 rounded-2xl border border-gray-700"
                  >
                    <p className="font-black text-yellow-300 break-words">
                      {a.name || "—"}
                    </p>
                    <p className="text-sm text-gray-300 break-words">
                      {a.email || "—"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mt-4">لا يوجد محامين مساعدين بعد</p>
            )}
          </div>
        </div>

        {/* ✅ Client Cases */}
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-black text-yellow-300 flex items-center gap-2">
              <FaGavel /> قضايا العميل
            </h3>

            <button
              onClick={() => navigate("/cases")}
              className="bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-xl font-bold flex items-center justify-center gap-2"
            >
              <FaEye /> فتح إدارة القضايا
            </button>
          </div>

          {clientCases?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {clientCases.map((cs) => (
                <button
                  key={cs._id}
                  onClick={() => navigate(`/cases/${cs._id}`)}
                  className="text-right bg-gray-800 hover:bg-gray-700 p-4 rounded-2xl border border-gray-700 hover:border-yellow-500 transition"
                >
                  <p className="font-black text-yellow-300 break-words">
                    {cs.title}
                  </p>
                  <p className="text-sm text-gray-300 mt-1">
                    ⚖️ {cs.type || "—"}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    📅 البداية:{" "}
                    {cs.startDate
                      ? new Date(cs.startDate).toLocaleDateString("ar-EG")
                      : "—"}
                  </p>

                  {cs.trialDate ? (
                    <p className="text-xs text-green-400 mt-1">
                      🏛️ أقرب جلسة:{" "}
                      {new Date(cs.trialDate).toLocaleDateString("ar-EG")}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      لا توجد جلسة محددة
                    </p>
                  )}

                  <p className="text-[11px] text-gray-500 mt-2">
                    🔑 كود القضية: {cs.caseCode || "—"}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">لا توجد قضايا مرتبطة بهذا العميل</p>
          )}
        </div>

        {/* Referred Users */}
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 mb-6">
          <h3 className="text-lg font-black text-yellow-300 mb-4 flex items-center gap-2">
            <FaUsers /> العملاء المنضمين من خلال الرابط
          </h3>

          {client.referredUsers?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {client.referredUsers.map((r, i) => (
                <div
                  key={i}
                  className="bg-gray-800 p-4 rounded-2xl border border-gray-700"
                >
                  <p className="font-black text-yellow-300 break-words">
                    {r.name || "—"}
                  </p>
                  <p className="text-sm text-gray-300 break-words">
                    {r.email || "—"}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">لا يوجد عملاء من خلال هذا الرابط</p>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="https://wa.me/201234567890"
            target="_blank"
            rel="noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl font-black flex items-center justify-center gap-2"
          >
            <FaPaperPlane /> مراسلة الإدارة
          </a>

          <button
            onClick={() => navigate("/clients")}
            className="bg-gray-800 hover:bg-gray-700 px-5 py-3 rounded-xl font-black"
          >
            رجوع
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-8 text-center">
          AlAvvocato © {new Date().getFullYear()} — Client Management Module
        </p>
      </div>
    </div>
  );
}

export default ClientDetails;
